import { toFixedNumber } from './toFixedNumber';

/**
 * Formats a number to show meaningful precision based on the first non-zero digit.
 *
 * This function intelligently formats numbers by:
 * - Showing "<0.00001" for very small numbers (< 0.00001)
 * - Using 5 decimal places for small numbers (< 0.1)
 * - Automatically calculating precision for numbers < 1 based on the exponent
 * - Using custom precision for numbers >= 1
 * - Removing trailing zeros from the fractional part
 *
 * @param number - The number to format
 * @param additionalPrecession - Optional additional precision to add (max 20 total)
 * @returns Formatted string representation of the number
 *
 * @example
 * ```typescript
 * formatToFirstNonZero(0)                    // returns "0"
 * formatToFirstNonZero(0.000001)             // returns "<0.00001"
 * formatToFirstNonZero(0.00001)              // returns "0.00001"
 * formatToFirstNonZero(0.0123456)            // returns "0.01234"
 * formatToFirstNonZero(0.5)                  // returns "0.5"
 * formatToFirstNonZero(1.234)                // returns "1"
 * formatToFirstNonZero(1.234, 2)             // returns "1.23"
 * formatToFirstNonZero(123.456)              // returns "123"
 * formatToFirstNonZero(123.456, 2)           // returns "123.45"
 * formatToFirstNonZero(1.2300, 2)            // returns "1.23" (trailing zeros removed)
 * ```
 */
export const formatToFirstNonZero = (number: number, additionalPrecession?: number) => {
  if (number === 0) return '0';

  if (Math.abs(number) < 0.00001) {
    return '<0.00001';
  }

  if (Math.abs(number) < 0.1) {
    const baseResult = String(toFixedNumber(number, additionalPrecession ?? 5));
    if (baseResult !== '0') {
      return baseResult;
    }
    return `<0.${'0'.repeat((additionalPrecession ?? 5) - 1)}1`;
  }

  const precession =
    number < 1
      ? additionalPrecession ??
        Math.abs(Number(number.toExponential().split('e').pop())) + (additionalPrecession || 0)
      : additionalPrecession || 0;
  const correctedPrecession = precession > 20 ? 20 : precession;

  const [integer, fractional] = number.toFixed(correctedPrecession || 0).split('.');

  const filteredFractional = fractional?.replace(/0+$/, '');

  const filtered = `${integer}${filteredFractional ? `.${filteredFractional}` : ''}`;

  return filtered;
};
